import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useTranslation } from "react-i18next";
import {
  Plus, Loader2, Calendar, MapPin, Users, Clock, ChevronRight,
  CheckCircle2, PlayCircle, PauseCircle, GraduationCap, Search,
} from "lucide-react";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import PageHeader from "../components/PageHeader";
import { ListSkeleton } from "../components/Skeletons";

const inputCls = "bg-[#0b0811] border border-white/10 text-white placeholder:text-slate-500 focus:border-[#f7c935]";

function sessionStatus(s) {
  const today = new Date().toISOString().slice(0, 10);
  const start = s.start_date || "";
  const end = s.end_date || start;
  if (end < today) return "terminee";
  if (start > today) return "a_venir";
  return "en_cours";
}

const STATUS_META = {
  a_venir: { label: "A venir", color: "#3b82f6", icon: PauseCircle },
  en_cours: { label: "En cours", color: "#10b981", icon: PlayCircle },
  terminee: { label: "Terminee", color: "#6b7280", icon: CheckCircle2 },
};

export default function SessionsPage() {
  const { axiosAuth } = useAuth();
  const { t } = useTranslation();
  const api = axiosAuth();
  const navigate = useNavigate();
  const [sessions, setSessions] = useState([]);
  const [programs, setPrograms] = useState([]);
  const [enrCounts, setEnrCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("en_cours");
  const [search, setSearch] = useState("");
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({
    program_id: "", title: "", start_date: "", end_date: "", location: "",
    modality: "presentiel", trainer_name: "", trainer_email: "",
    max_participants: 10, min_participants: 1,
    price_ht: 0, price_particulier: 0, price_independant: 0,
    financement: "", opco: "", notes: "", tags: "",
  });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [s, p] = await Promise.all([
        api.get("/lms/sessions"),
        api.get("/lms/programs"),
      ]);
      const sess = s.data || [];
      setSessions(sess);
      setPrograms(p.data || []);
      // Fetch enrollment counts in parallel (best-effort)
      const counts = {};
      await Promise.all(sess.map(async (ss) => {
        try {
          const r = await api.get("/lms/sessions/" + ss.session_id + "/enrollments");
          counts[ss.session_id] = (r.data || []).length;
        } catch { counts[ss.session_id] = 0; }
      }));
      setEnrCounts(counts);
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
    setLoading(false);
  // eslint-disable-next-line
  }, []);

  useEffect(() => { load(); }, [load]);

  const create = async () => {
    if (!form.program_id || !form.start_date || !form.end_date) { toast.error("Programme + dates requis"); return; }
    if (form.end_date < form.start_date) { toast.error("La date de fin doit etre posterieure a la date de debut"); return; }
    try {
      const payload = { ...form, tags: form.tags ? form.tags.split(",").map((t) => t.trim()).filter(Boolean) : [] };
      const r = await api.post("/lms/sessions", payload);
      toast.success("Session creee — modules, evaluations et docs Qualiopi auto-importes");
      setShowCreate(false);
      setForm({ program_id: "", title: "", start_date: "", end_date: "", location: "", modality: "presentiel", trainer_name: "", trainer_email: "", max_participants: 10, min_participants: 1, price_ht: 0, price_particulier: 0, price_independant: 0, financement: "", opco: "", notes: "", tags: "" });
      navigate("/admin/formation/sessions/" + r.data.session_id);
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
  };

  // Auto-fill from program when selected
  const onSelectProgram = (program_id) => {
    const prog = programs.find((p) => p.program_id === program_id);
    setForm((f) => ({
      ...f,
      program_id,
      title: prog ? prog.title : f.title,
      price_ht: prog?.price_entreprise ?? prog?.price_ht ?? f.price_ht,
      price_particulier: prog?.price_particulier ?? f.price_particulier,
      price_independant: prog?.price_independant ?? f.price_independant,
    }));
  };

  const buckets = useMemo(() => {
    const out = { a_venir: [], en_cours: [], terminee: [] };
    const q = search.trim().toLowerCase();
    for (const s of sessions) {
      if (q && !((s.title || "").toLowerCase().includes(q) || (s.program_title || "").toLowerCase().includes(q) || (s.location || "").toLowerCase().includes(q))) continue;
      out[sessionStatus(s)].push(s);
    }
    return out;
  }, [sessions, search]);

  const list = buckets[tab] || [];

  return (
    <div className="p-6 max-w-7xl mx-auto" data-testid="sessions-page">
      <PageHeader
        icon={<GraduationCap />}
        title={`Pilotage ${t("sessions.title").toLowerCase()}`}
        description="Créez une session à partir d'un programme — modules, évaluations et docs Qualiopi sont auto-importés."
        breadcrumb={[{ label: t("nav.sessions") }]}
        testId="sessions-header"
        actions={(
          <Button onClick={() => setShowCreate(true)} className="bg-[#f7c935] text-[#14101c] font-bold" data-testid="session-new-btn">
            <Plus className="w-4 h-4 mr-2" /> {t("sessions.new_session")}
          </Button>
        )}
      />

      {/* Status tabs */}
      <div className="flex gap-2 mb-4 flex-wrap">
        {(["a_venir", "en_cours", "terminee"]).map((k) => {
          const meta = STATUS_META[k];
          const Icon = meta.icon;
          const active = tab === k;
          return (
            <button key={k} onClick={() => setTab(k)}
              data-testid={"sessions-tab-" + k}
              className={"flex items-center gap-2 px-4 py-2 rounded-full text-xs font-bold transition-colors border " + (active ? "border-transparent text-[#14101c]" : "bg-[#0b0811] border-white/10 text-slate-400 hover:text-white")}
              style={active ? { background: meta.color } : {}}
            >
              <Icon className="w-3.5 h-3.5" />{meta.label} ({buckets[k].length})
            </button>
          );
        })}
        <div className="ml-auto relative">
          <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
          <Input placeholder="Rechercher..." value={search} onChange={(e) => setSearch(e.target.value)} className={inputCls + " pl-9 h-9 w-56"} data-testid="sessions-search" />
        </div>
      </div>

      {loading ? (
        <ListSkeleton items={4} testId="sessions-loading-skeleton" />
      ) : list.length === 0 ? (
        <div className="text-center py-20 border border-dashed border-white/10 rounded-xl bg-[#0b0811]">
          <GraduationCap className="w-10 h-10 mx-auto text-slate-700 mb-3" />
          <p className="text-sm text-slate-500">Aucune session {STATUS_META[tab].label.toLowerCase()}.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid={"sessions-grid-" + tab}>
          {list.map((s) => {
            const meta = STATUS_META[sessionStatus(s)];
            return (
              <Link key={s.session_id} to={"/admin/formation/sessions/" + s.session_id}
                className="group text-left bg-[#14101c] border border-white/5 rounded-xl p-5 hover:border-[#f7c935]/30 transition-colors block" data-testid={"session-card-" + s.session_id}>
                <div className="flex items-start justify-between mb-2">
                  <div className="font-bold text-white text-base truncate flex-1">{s.title || s.program_title}</div>
                  <span className="text-[9px] px-1.5 py-0.5 rounded font-bold uppercase" style={{ background: meta.color + "20", color: meta.color }}>{meta.label}</span>
                </div>
                <div className="text-xs text-slate-400 mb-3">{s.program_title}</div>
                <div className="space-y-1 text-[11px] text-slate-500">
                  <div className="flex items-center gap-1.5"><Calendar className="w-3 h-3" />{s.start_date} → {s.end_date}</div>
                  {s.location && <div className="flex items-center gap-1.5"><MapPin className="w-3 h-3" />{s.location}</div>}
                  {s.trainer_name && <div className="flex items-center gap-1.5"><Users className="w-3 h-3" />{s.trainer_name}</div>}
                  <div className="flex items-center gap-1.5"><Clock className="w-3 h-3" />{s.program_duration_hours || 0}h · {enrCounts[s.session_id] ?? "-"} / {s.max_participants} stagiaires</div>
                </div>
                <div className="flex justify-between items-center mt-3 pt-3 border-t border-white/5 text-xs text-slate-500 group-hover:text-[#f7c935] transition-colors">
                  <span>Ouvrir le pilotage</span>
                  <ChevronRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
                </div>
              </Link>
            );
          })}
        </div>
      )}

      {showCreate && (
        <Dialog open onOpenChange={(o) => { if (!o) setShowCreate(false); }}>
          <DialogContent className="max-w-2xl bg-[#0b0811] border-white/10 text-white max-h-[90vh] overflow-y-auto">
            <DialogHeader><DialogTitle>Nouvelle session de formation</DialogTitle></DialogHeader>
            <div className="space-y-4">
              {/* Programme */}
              <div>
                <label className="text-[10px] uppercase tracking-wider text-[#f7c935] font-bold block mb-1">Programme (produit formation) *</label>
                <select value={form.program_id} onChange={(e) => onSelectProgram(e.target.value)} className={inputCls + " px-3 py-2 rounded w-full"} data-testid="sess-program">
                  <option value="">— Selectionner un programme —</option>
                  {programs.map((p) => <option key={p.program_id} value={p.program_id}>{p.title}</option>)}
                </select>
                <p className="text-[10px] text-slate-500 mt-1">Les modules de cours, evaluations et docs Qualiopi rattaches au programme seront auto-importes. Les tarifs seront pre-remplis.</p>
              </div>

              {/* Titre + tags */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <label className="text-[10px] text-slate-500 block mb-1">Titre personnalise</label>
                  <Input placeholder="Auto depuis le programme si vide" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className={inputCls} data-testid="sess-title" />
                </div>
                <div>
                  <label className="text-[10px] text-slate-500 block mb-1">Tags (virgules)</label>
                  <Input placeholder="intra, inter, sur-mesure" value={form.tags} onChange={(e) => setForm({ ...form, tags: e.target.value })} className={inputCls} data-testid="sess-tags" />
                </div>
              </div>

              {/* Dates + modalite */}
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="text-[10px] text-slate-500 block mb-1">Debut *</label>
                  <Input type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} className={inputCls} data-testid="sess-start" />
                </div>
                <div>
                  <label className="text-[10px] text-slate-500 block mb-1">Fin *</label>
                  <Input type="date" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} className={inputCls} data-testid="sess-end" />
                </div>
                <div>
                  <label className="text-[10px] text-slate-500 block mb-1">Modalite</label>
                  <select value={form.modality} onChange={(e) => setForm({ ...form, modality: e.target.value })} className={inputCls + " px-3 py-2 rounded w-full"} data-testid="sess-modality">
                    <option value="presentiel">Presentiel</option>
                    <option value="distanciel">Distanciel</option>
                    <option value="mixte">Mixte</option>
                    <option value="elearning">E-learning</option>
                  </select>
                </div>
              </div>

              {/* Lieu + formateur */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <label className="text-[10px] text-slate-500 block mb-1">Lieu</label>
                  <Input placeholder="Adresse, visio..." value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} className={inputCls} data-testid="sess-location" />
                </div>
                <div>
                  <label className="text-[10px] text-slate-500 block mb-1">Formateur (nom)</label>
                  <Input placeholder="Prenom Nom" value={form.trainer_name} onChange={(e) => setForm({ ...form, trainer_name: e.target.value })} className={inputCls} data-testid="sess-trainer" />
                </div>
                <div>
                  <label className="text-[10px] text-slate-500 block mb-1">Email formateur</label>
                  <Input type="email" placeholder="formateur@exemple.com" value={form.trainer_email} onChange={(e) => setForm({ ...form, trainer_email: e.target.value })} className={inputCls} />
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="text-[10px] text-slate-500 block mb-1">Min. participants</label>
                    <Input type="number" min={1} value={form.min_participants} onChange={(e) => setForm({ ...form, min_participants: parseInt(e.target.value || "1") })} className={inputCls} />
                  </div>
                  <div>
                    <label className="text-[10px] text-slate-500 block mb-1">Max. participants</label>
                    <Input type="number" min={1} value={form.max_participants} onChange={(e) => setForm({ ...form, max_participants: parseInt(e.target.value || "1") })} className={inputCls} data-testid="sess-max" />
                  </div>
                </div>
              </div>

              {/* Tarifs 3 segments + financement */}
              <div>
                <div className="text-[10px] uppercase tracking-wider text-[#f7c935] font-bold mb-1">Tarifs HT (3 segments)</div>
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <label className="text-[10px] text-slate-500 block mb-1">Entreprise</label>
                    <Input type="number" value={form.price_ht} onChange={(e) => setForm({ ...form, price_ht: parseFloat(e.target.value || "0") })} className={inputCls} data-testid="sess-price-ent" />
                  </div>
                  <div>
                    <label className="text-[10px] text-slate-500 block mb-1">Particulier</label>
                    <Input type="number" value={form.price_particulier} onChange={(e) => setForm({ ...form, price_particulier: parseFloat(e.target.value || "0") })} className={inputCls} data-testid="sess-price-part" />
                  </div>
                  <div>
                    <label className="text-[10px] text-slate-500 block mb-1">Independant</label>
                    <Input type="number" value={form.price_independant} onChange={(e) => setForm({ ...form, price_independant: parseFloat(e.target.value || "0") })} className={inputCls} />
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-[10px] text-slate-500 block mb-1">Financement par defaut</label>
                  <select value={form.financement} onChange={(e) => setForm({ ...form, financement: e.target.value })} className={inputCls + " px-3 py-2 rounded w-full"}>
                    <option value="">—</option>
                    <option value="autofinance">Autofinance</option>
                    <option value="cpf">CPF</option>
                    <option value="opco">OPCO</option>
                    <option value="pole_emploi">Pole Emploi</option>
                    <option value="entreprise">Entreprise</option>
                    <option value="subrogation">Subrogation</option>
                  </select>
                </div>
                <div>
                  <label className="text-[10px] text-slate-500 block mb-1">OPCO (si applicable)</label>
                  <Input placeholder="ex: AFDAS, OPCO EP..." value={form.opco} onChange={(e) => setForm({ ...form, opco: e.target.value })} className={inputCls} />
                </div>
              </div>

              <div>
                <label className="text-[10px] text-slate-500 block mb-1">Notes internes</label>
                <textarea rows={3} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className={inputCls + " w-full px-3 py-2 rounded resize-none"} placeholder="Informations utiles a l'equipe..." />
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-4 pt-3 border-t border-white/5">
              <Button variant="ghost" onClick={() => setShowCreate(false)}>Annuler</Button>
              <Button onClick={create} className="bg-[#f7c935] text-[#14101c] font-bold" data-testid="sess-create-confirm">Creer la session</Button>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
